import { create } from 'zustand';
import { DEFAULT_SETTINGS, PALETTES } from '../utils/constants';
import { applyTheme } from '../utils/helpers';
import { PAGE_PERMS, keyAllowedByModules } from '../utils/permissions';
import { NAV } from '../utils/sidebarNav';
import * as schoolStorage from '../utils/schoolStorage';

const SYNCABLE_KEYS = new Set([
  'challanSettings', 'currencySymbol', 'schoolName', 'schoolCity',
  'principalName', 'phone', 'email', 'address', 'rollFormat', 'familyIdFormat',
  'admissionPrefix', 'receiptSettings', 'schoolLogoUrl',
  'statuses', 'religions', 'nationalities', 'castList', 'occupations',
  'rollResetOnPromotion', 'rollNumberSchema', 'rollSchoolPrefix',
  'weeklyOffDays', 'dateFormat', 'statCardsMinimized', 'financeQuickPicks',
]);

// Browser storage rules (see utils/schoolStorage.js):
//  - data that belongs to a school (settings, challan records, exam category picks...) is stored under
//    `${schoolId}:<key>` and loaded by hydrateForSchool() once the session knows the active campus;
//  - per-device preferences (colour theme, last page...) use plain localStorage.
const THEME_KEYS = ['paletteId', 'themeVars', 'activeTheme'];
// A device that saved a theme which no longer exists falls back to the default (PALETTES[0]).
const readDeviceTheme = () => {
  try {
    const t = JSON.parse(localStorage.getItem('theme'));
    if (!t || typeof t !== 'object') return {};
    if (t.paletteId && !PALETTES.some(p => p.id === t.paletteId)) return {};
    return t;
  } catch { return {}; }
};
// First run after this change: the theme used to live inside appSettings. Keep it, per device.
try {
  if (!localStorage.getItem('theme')) {
    const legacy = JSON.parse(localStorage.getItem('appSettings'));
    if (legacy) {
      const t = {};
      THEME_KEYS.forEach(k => { if (k in legacy) t[k] = legacy[k]; });
      if (Object.keys(t).length) localStorage.setItem('theme', JSON.stringify(t));
    }
  }
} catch { /* storage blocked */ }

// appSettings belongs to the school; the colour theme inside it is mirrored to the device.
const persistSettings = (settings) => {
  schoolStorage.setItem('appSettings', JSON.stringify(settings));
  const theme = {};
  THEME_KEYS.forEach(k => { if (k in settings) theme[k] = settings[k]; });
  if (Object.keys(theme).length) { try { localStorage.setItem('theme', JSON.stringify(theme)); } catch { /* storage blocked */ } }
};
const readSchoolJson = (key, schoolId, fallback) => {
  try { const v = JSON.parse(schoolStorage.getItem(key, schoolId)); return v == null ? fallback : v; } catch { return fallback; }
};

// Retired browser-only state; the server tables are the source of truth now.
try { localStorage.removeItem('inventory'); localStorage.removeItem('posCredits'); localStorage.removeItem('feeRecords'); } catch {}

// One rule for "may this user do this", used by hasPermission and by the page reset in setUser.
// Owner/director are read-only in the DB: only '.view' keys that are ticked count for them.
// Platform admins pass everything, except modules this school does not have (hidden for everyone).
export function userHasPermission(u, key) {
  if (!u) return false;
  if (!keyAllowedByModules(u.enabledModules, key)) return false;
  if (u.isPlatformAdmin) return true;
  if (u.roleName === 'owner' || u.roleName === 'director') {
    return key.endsWith('.view') && !!u.permissions?.[key];
  }
  return !!u.permissions?.[key];
}

// The first page in sidebar order that this user may open, or null when there is none.
function firstAllowedPage(u) {
  for (const group of NAV) {
    for (const item of group.items) {
      if (userHasPermission(u, item.perm)) return item.id;
    }
  }
  return null;
}

const useStore = create((set, get) => ({
  // ─── AUTH ─────────────────────────────────────────────
  user: null,
  setUser: (user) => {
    const currentPage = get().page;
    const requiredPerm = PAGE_PERMS[currentPage];
    // Platform admins (superadmin acting inside a school) get every page —
    // has_permission() already returns true for them in the DB regardless
    // of that school's admin-role configuration.
    // Same rule as hasPermission. A page that has no entry in PAGE_PERMS is unknown and falls back to the dashboard;
    // the platform panel is the one page guarded by isPlatformAdmin instead of a key.
    const canAccess = String(currentPage || '').startsWith('platform')
      ? !!user?.isPlatformAdmin
      : !!requiredPerm && userHasPermission(user, requiredPerm);
    // Sign-out, a different account, or a different school: the cached exam data belongs to the old one
    const prev = get().user;
    const switched = (prev?.id || null) !== (user?.id || null) || (prev?.schoolId || null) !== (user?.schoolId || null);
    const wipe = switched ? { examsCache: null, examsCacheGen: (get().examsCacheGen || 0) + 1 } : {};
    if (!canAccess) {
      // Land on the first page this user can open, in sidebar order, instead of an Access Denied screen.
      const landing = firstAllowedPage(user) || 'dashboard';
      try { localStorage.setItem('lastPage', landing); } catch {}
      set({ user, page: landing, ...wipe });
    } else {
      set({ user, ...wipe });
    }
  },
  // Owner/director are read-only in the DB now — has_permission() there
  // only ever returns true for them on '.view' keys, and only if ticked;
  // every write is blocked by RLS regardless of what this returns. Mirror
  // that here so the UI doesn't offer actions the server will reject.
  // Platform admins are the one exception to all of this — has_permission()
  // already returns true for every key for them, in any school.
  hasPermission: (key) => userHasPermission(get().user, key),

  // A message for the login screen, set when a session had to be ended (for example the account could not be loaded).
  loginNotice: '',
  setLoginNotice: (loginNotice) => set({ loginNotice }),

  // Forget everything that belonged to the person who is leaving, so the next user never sees or inherits it.
  resetSessionData: () => {
    try { localStorage.removeItem('lastPage'); } catch {}
    set(s => ({
      page: 'dashboard', pendingAction: null,
      students: [], accounts: [], classes: [], feeStructures: {}, products: [], productStock: {},
      feesCache: {}, challanRecords: {}, challanHistory: [], discountLedger: [], challanSeq: 0,
      examsCache: null, examsCacheGen: (s.examsCacheGen || 0) + 1, sharedExamTypes: [],
      examLastScheduleCat: null, examLastMarkEntryCat: null, examLastResultsCat: null,
      settings: { ...s.settings, feeHeads: [] },
    }));
  },

  // ─── NAVIGATION ───────────────────────────────────────
  page: (() => { try { return localStorage.getItem('lastPage') || 'dashboard'; } catch { return 'dashboard'; } })(),
  pendingAction: null,
  setPage: (page, action = null) => { try { localStorage.setItem('lastPage', page); } catch {} set({ page, pendingAction: action }); },
  clearPendingAction: () => set({ pendingAction: null }),

  // ─── LOADING ──────────────────────────────────────────
  dbLoading: true,
  setDbLoading: (dbLoading) => set({ dbLoading }),

  // ─── ACCOUNTS (chart of accounts, loaded on app start) ─
  accounts: [],
  setAccounts: (accounts) => set({ accounts }),
  // Look up an account UUID by its code (e.g. accountId('1000') → Cash)
  accountId: (code) => {
    const acc = get().accounts.find(a => a.code === code);
    return acc?.id || null;
  },

  // ─── CLASSES ──────────────────────────────────────────
  classes: [],
  setClasses: (classes) => set({ classes }),

  // ─── FEE STRUCTURES ───────────────────────────────────
  feeStructures: {},
  setFeeStructures: (feeStructures) => set({ feeStructures }),

  // ─── FEES REFRESH ─────────────────────────────────────
  feesRefreshTick: 0,
  bumpFeesRefresh: () => set(s => ({
    feesRefreshTick: s.feesRefreshTick + 1,
    feesCache: {},
  })),

  // ─── FEES CACHE ───────────────────────────────────────
  // Keyed by 'schoolId:month:year'. Survives tab switches and navigation;
  // cleared on any bumpFeesRefresh (payment, void, waiver, generate, close).
  feesCache: {},
  setFeesCache: (key, data) => set(s => ({
    feesCache: { ...s.feesCache, [key]: { data, ts: Date.now() } },
  })),
  clearFeesCache: () => set({ feesCache: {} }),

  // ─── STUDENTS ─────────────────────────────────────────
  students: [],
  setStudents: (students) => set({ students }),
  addStudent: (student) => set(s => ({ students: [...s.students, student] })),
  updateStudent: (id, data) => set(s => ({
    students: s.students.map(st => st.id === id ? { ...st, ...data } : st)
  })),
  deleteStudent: (id) => set(s => ({
    students: s.students.filter(st => st.id !== id)
  })),

  // ─── SETTINGS ─────────────────────────────────────────
  // Starts from defaults plus this device's theme; the school's own saved settings arrive in hydrateForSchool().
  settings: { ...DEFAULT_SETTINGS, ...readDeviceTheme() },
  hydrateForSchool: (schoolId) => {
    if (!schoolId) return;
    // Data saved before per-school storage belongs to the original school only (copied, never deleted).
    schoolStorage.adoptLegacyKeys(schoolId);
    let saved = null;
    try { saved = JSON.parse(schoolStorage.getItem('appSettings', schoolId)); } catch { saved = null; }
    const settings = { ...DEFAULT_SETTINGS, ...(saved || {}), ...readDeviceTheme() };
    if (settings.paletteId && !PALETTES.some(p => p.id === settings.paletteId)) {
      settings.paletteId = PALETTES[0].id;
      settings.themeVars = PALETTES[0].vars;
    }
    if (settings.themeVars) applyTheme(settings.themeVars);
    set({
      settings,
      challanRecords: readSchoolJson('challanRecords', schoolId, {}),
      challanHistory: readSchoolJson('challanHistory', schoolId, []),
      examLastScheduleCat: schoolStorage.getItem('examLastScheduleCat', schoolId) || null,
      examLastMarkEntryCat: schoolStorage.getItem('examLastMarkEntryCat', schoolId) || null,
      examLastResultsCat: schoolStorage.getItem('examLastResultsCat', schoolId) || null,
    });
  },
  setSettings: (settings) => {
    if (settings.themeVars) applyTheme(settings.themeVars);
    else applyTheme(PALETTES[0].vars);
    persistSettings(settings);
    const { user } = get();
    const schoolId = user?.schoolId;
    const userId = user?.id;
    const syncable = Object.entries(settings).filter(([k]) => SYNCABLE_KEYS.has(k));
    if (schoolId && syncable.length) {
      import('../db').then(({ saveSetting }) => {
        syncable.forEach(([k, v]) => saveSetting(schoolId, k, v, userId).catch(console.error));
      }).catch(console.error);
    }
    set({ settings });
  },
  loadSettings: (dbSettings) => set(s => ({ settings: { ...s.settings, ...dbSettings } })),
  updateSettings: (updates) => set(s => {
    const next = { ...s.settings, ...updates };
    persistSettings(next);
    const schoolId = s.user?.schoolId;
    const userId = s.user?.id;
    const syncable = Object.entries(updates).filter(([k]) => SYNCABLE_KEYS.has(k));
    if (schoolId && syncable.length) {
      import('../db').then(({ saveSetting }) => {
        syncable.forEach(([k, v]) => saveSetting(schoolId, k, v, userId).catch(console.error));
      }).catch(console.error);
    }
    return { settings: next };
  }),

  // ─── PRODUCTS (DB-backed) ────────────────────────────
  products: [],
  productStock: {},
  setProducts:     (products)     => set({ products }),
  setProductStock: (productStock) => set({ productStock }),

  // ─── DISCOUNT LEDGER ──────────────────────────────────
  discountLedger: [],
  setDiscountLedger: (discountLedger) => set({ discountLedger }),
  addDiscount: (discount) => set(s => ({
    discountLedger: [...s.discountLedger, discount]
  })),

  // ─── CHALLAN RECORDS ──────────────────────────────────
  challanRecords: {},
  setChallanRecords: (challanRecords) => {
    schoolStorage.setItem('challanRecords', JSON.stringify(challanRecords));
    set({ challanRecords });
  },

  // ─── CHALLAN HISTORY ──────────────────────────────────
  challanHistory: [],
  setChallanHistory: (challanHistory) => {
    schoolStorage.setItem('challanHistory', JSON.stringify(challanHistory));
    set({ challanHistory });
  },

  // ─── CHALLAN SEQUENCE (server-side, per school) ───────
  // Previously a per-browser localStorage counter: every machine started at 0, so
  // two admins collecting fees on different computers would both issue challan #1,
  // #2, #3 — duplicate numbers on real financial documents, silently. The counter
  // now lives on schools.next_challan_no and is incremented atomically server-side
  // (UPDATE ... RETURNING takes a row lock), so numbers are unique per school no
  // matter how many people are collecting at once.
  // NOTE: this is now ASYNC — callers must await it.
  challanSeq: 0,
  nextChallanSeq: async () => {
    const schoolId = get().user?.schoolId;
    if (!schoolId) throw new Error('No school context for challan numbering');
    const { supabase } = await import('../supabase');
    const { data, error } = await supabase.rpc('get_next_challan_no', { p_school_id: schoolId });
    if (error) throw error;
    set({ challanSeq: data });
    return data;
  },

  // ─── EXAM TAB PERSISTENCE ─────────────────────────────
  examLastTab: (() => { try { return localStorage.getItem('examLastTab') || 'schedule'; } catch { return 'schedule'; } })(),
  setExamLastTab: (tab) => { try { localStorage.setItem('examLastTab', tab); } catch {} set({ examLastTab: tab }); },
  examLastScheduleCat: null,
  setExamLastScheduleCat: (id) => { schoolStorage.setItem('examLastScheduleCat', id); set({ examLastScheduleCat: id }); },
  examLastMarkEntryCat: null,
  setExamLastMarkEntryCat: (id) => { schoolStorage.setItem('examLastMarkEntryCat', id); set({ examLastMarkEntryCat: id }); },
  examLastResultsCat: null,
  setExamLastResultsCat: (id) => { schoolStorage.setItem('examLastResultsCat', id); set({ examLastResultsCat: id }); },
  sharedExamTypes: [],
  setSharedExamTypes: (types) => set({ sharedExamTypes: types }),
  // The exams, class status and subject names the three Exams tabs share, so switching tabs does not refetch them.
  // { schoolId, exams, classStatus, subjectNames, loadedAt }. Not persisted. Anything that changes an exam clears it.
  examsCache: null,
  examsCacheGen: 0,   // bumped by every clear, so a load that started before a clear never stores its stale result
  setExamsCache: (data) => set({ examsCache: data }),
  clearExamsCache: () => set(s => ({ examsCache: null, examsCacheGen: (s.examsCacheGen || 0) + 1 })),
}));

export default useStore;